<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { SearchX, Wand2, RotateCcw } from 'lucide-svelte';

  import { getDefaultFilters } from '$lib/api/filters';
  import { applicationMode } from '$lib/stores/mode';
  import { queryFilters } from '$lib/stores/selection';
  import { selectedIndustryCentroids } from '$lib/stores/data';
  import { guidedActive, resetGuided } from '$lib/stores/guided';
  import { mixedResults } from '$lib/stores/mixedResults';

  /**
   * A search that matched nothing.
   *
   * # What was here
   *
   * A 128px circle under a pulsing gradient, two more circles floating and
   * rotating on a three-second loop, an orange badge, an indigo button carrying
   * `style="background: #4f46e5 !important"` past the shared styles, and the
   * whole thing on a grey field that no other screen uses. It was the loudest
   * page in the app, and it is the one shown when the app has nothing to give.
   *
   * # Both of its buttons did nothing useful
   *
   * "Clear all" ran `document.querySelector('[data-reset-filters]')?.click()`,
   * and no component in this codebase renders that attribute — so it was a
   * no-op that looked like a control. The other called
   * `window.location.reload()`, which in a single-page app throws away the
   * selection the customer built and returns them to the front door; on the one
   * screen whose job is "your selection is too narrow", reloading is the worst
   * of the available actions.
   *
   * What is offered instead is the two things that actually help: go back to
   * the guide and widen the search, or clear the filters and start again.
   * Neither runs a search — an empty filter set matched against the whole
   * database is a request nobody meant to make.
   */

  function backToTheGuide() {
    resetGuided();
    guidedActive.set(true);
    mixedResults.set({ companies: [], metadata: {}, similarityBatches: [], loading: false });
  }

  function clearFilters() {
    queryFilters.set(getDefaultFilters());
    selectedIndustryCentroids.set(new Map());
    mixedResults.set({ companies: [], metadata: {}, similarityBatches: [], loading: false });
  }

  const tips = ['broaderTerms', 'removeFilters', 'checkSpelling', 'expandRegion'];
</script>

<div class="w-full max-w-md text-center">
  <div
    class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-surface bg-surface-sunken"
  >
    <SearchX class="h-6 w-6 text-gray-400" />
  </div>

  <h2 class="mb-1.5 text-base font-semibold text-gray-900">{$_('headings.noResultsFound')}</h2>
  <p class="mb-6 text-sm text-gray-600">{$_('status.adjustSearchTerms')}</p>

  <div class="mb-6 flex items-center justify-center gap-2">
    {#if $applicationMode === 'classic'}
      <button
        type="button"
        on:click={backToTheGuide}
        class="inline-flex items-center gap-1.5 rounded-control bg-accent-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent-700"
      >
        <Wand2 class="h-4 w-4" />
        {$_('guided.guideMe')}
      </button>
    {/if}

    <button
      type="button"
      on:click={clearFilters}
      class="inline-flex items-center gap-1.5 rounded-control border border-hairline bg-surface-panel px-3.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-hairline-strong hover:text-gray-900"
    >
      <RotateCcw class="h-4 w-4" />
      {$_('buttons.clearAll')}
    </button>
  </div>

  <!--
    The tips stay: they are the only part of this screen that told a customer
    what to try next. They read as a note now rather than as a panel — the
    screen is already a dead end and a bordered box inside it made two.
  -->
  <div class="border-t border-hairline pt-4 text-left">
    <p class="mb-2 text-2xs font-semibold uppercase tracking-wide text-gray-500">
      {$_('status.searchTips')}
    </p>
    <ul class="list-inside list-disc space-y-1 text-xs text-gray-600 marker:text-gray-300">
      {#each tips as tip (tip)}
        <li>{$_(`status.${tip}`)}</li>
      {/each}
    </ul>
  </div>
</div>
